import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatMenuModule } from '@angular/material/menu';
import {
  catchError,
  concat,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  lastValueFrom,
  Observable,
  of,
  pairwise,
  startWith,
  Subscription,
  take,
  toArray,
} from 'rxjs';
import {
  AdditionalValuesFiles,
  ErrorInpusGeneral,
  FormGeneral,
  GeneralForm,
  InputsGeneralForm,
} from '../../../shared/models/pages-model/form-general.model';
import { RuleInputForm } from '../../../shared/models/pages-model/page-general.model';
import { Toll } from '../../../shared/models/components-general/menu-toll.model';
import { ButtonFunctionService } from '../../../shared/services/button-function.service';
import { GeneralService } from '../../../shared/services/general.service';
import { DynamicPageService } from '../../../shared/services/dynamic-page.service';
import { LoadGeneralComponent } from '../../../shared/components-generals/load-general/load-general.component';
import { InputGeneral } from '../../../shared/inputs/input-general/input-general';
import { InputFileLightComponent } from '../../../shared/inputs/input-file-light/input-file-light.component';
import { InputGeneralCalendarComponent } from '../../../shared/inputs/input-general-calendar/input-general-calendar.component';
import { InputGeneralSelectComponent } from '../../../shared/inputs/input-general-select/input-general-select.component';
import { InputGeneralAutocompleteComponent } from '../../../shared/inputs/input-general-autocomplete/input-general-autocomplete.component';
import { InputGeneralRadioButtonComponent } from '../../../shared/inputs/input-general-radio-button/input-general-radio-button.component';
import { InputGeneralTextComponent } from '../../../shared/inputs/input-general-text/input-general-text.component';
import { ReaderPdfComponent } from '../../../shared/inputs/reader-pdf/reader-pdf.component';
import { ReaderTableComponent } from '../../../shared/inputs/reader-table/reader-table.component';
import { ToolsMenuComponent } from '../../../shared/components-generals/tools-menu/tools-menu.component';

@Component({
  selector: 'app-form-general',
  standalone: true,
  templateUrl: './form-general.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatExpansionModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatCheckboxModule,
    MatMenuModule,
    LoadGeneralComponent,
    InputGeneral,
    InputFileLightComponent,
    InputGeneralCalendarComponent,
    InputGeneralSelectComponent,
    InputGeneralAutocompleteComponent,
    InputGeneralRadioButtonComponent,
    InputGeneralTextComponent,
    ReaderPdfComponent,
    ReaderTableComponent,
    ToolsMenuComponent,
  ],
})
export class FormGeneralComponent {
  public subscribeArray: Subscription[] = [];
  public formGroup: FormGroup = new FormGroup({});
  @Input() generateForm: FormGeneral = {
    formGroups: null,
  };
  @Input() ruleInputForm: RuleInputForm = {};
  @Input() collectionFormsControls: { [key: string]: FormControl } = {};
  @Input() toll = false;
  @Input() changeStaus13 = false; // harcodeade para cambio de status, eliminar cuando se pueda hacer con reglas
  @Input() idSection = 0;
  @Input() edit = false;
  @Input() isExpasion = true;
  @Input() quotationId = 0;
  @Input() hidenScrollRight = false;
  @Input() hidenScrollLeft = false;
  @Input() tollArray: Toll[] = [];
  @Output() emitFunctionTool = new EventEmitter<{
    toll: Toll;
    formGroups: GeneralForm;
  }>();
  @Output() emitButton = new EventEmitter<string>();
  private collectionFormsControlsOrder: { [key: string]: FormControl } = {};
  public loadService = false;

  constructor(
    private fb: FormBuilder,
    private buttonFunctionService: ButtonFunctionService,
    private dynamicPageService: DynamicPageService,
    private generalService: GeneralService,
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.unsubscribeAll();
  }

  // Se suscribe a todos los Formularios
  public subscribeForm(): void {
    Object.keys(this.formGroup.controls).forEach((controlName) => {
      const control = this.formGroup.get(controlName);
      if (control) {
        const sub = control.valueChanges
          .pipe(
            startWith(control.value), // Emitir el valor inicial
            debounceTime(700), // Esperar 700 ms tras cada cambio
            distinctUntilChanged(), // Ignorar valores idénticos
            pairwise(), // Obtener valor anterior y actual
            filter(([prev, current]) => JSON.stringify(prev) !== JSON.stringify(current)), // Validar si realmente cambió
          )
          .subscribe(([prev, current]) => {});

        this.subscribeArray.push(sub); // Guardar la suscripción
      }
    });
  }

  // Funcion para desuscribirse de los formularios
  public unsubscribeAll(): void {
    this.subscribeArray.forEach((sub) => sub.unsubscribe());
    this.subscribeArray = []; // Limpia el arreglo después de desuscribirse
  }

  // Crea los contrroladores de los Inputs del Formulario
  public createFormPage(): {
    form: FormGroup;
    collectionFormsControls: { [key: string]: FormControl<any> };
  } {
    this.collectionFormsControls = {};
    this.formGroup = new FormGroup({});
    this.unsubscribeAll();
    // Crea un nuevo FormGroup para cada actividad
    const formGroupControl = new FormGroup({});
    // Crea un nuevo FormGroup para cada sección
    const sectionGroup = new FormGroup({});
    const section = this.generateForm.formGroups;
    section?.multifrom.forEach((multifrom, index) => {
      multifrom.fields.forEach((inputField, index) => {
        let control = new FormControl('');
        // Crea un nuevo FormControl para cada campo en `from`
        inputField.error !== undefined ? (control = this.generateError(inputField.error)) : null;
        // deshabilita input si se requiere
        const ruleKey = `${inputField.idControl}-${this.generateForm.formGroups?.idForm}`;
        const ruleForm = this.ruleInputForm ? this.ruleInputForm[ruleKey] : undefined;
        inputField.disabled || (ruleForm && ruleForm.disabled) ? control.disable() : null;
        // poblar con datos si existe
        inputField.value ? control.setValue(inputField.value) : control.setValue(null);

        this.collectionFormsControls[inputField.idFullControl] === undefined
          ? (this.collectionFormsControls[inputField.idFullControl] = control)
          : null;

        if (!sectionGroup.contains(inputField.idFullControl)) {
          // Añade el control al FormGroup de la sección
          sectionGroup.addControl(
            `${inputField.idFullControl}`,
            this.collectionFormsControls[inputField.idFullControl],
          );
        } else {
          this.generalService.viewSnacAlert(
            'Un capo se a sobrescrito',
            'alert',
            `revisar json ${inputField.idFullControl}`,
          );
        }

        // Añade el FormControl al campo `from` original para referencia
        inputField.formControl = this.collectionFormsControls[inputField.idFullControl];
      });

      // Añade el FormGroup de la sección al FormGroup principal validando que no se repita
      if (!formGroupControl.contains(`${section.idForm}`)) {
        formGroupControl.addControl(`${section.idForm}`, sectionGroup);
      }
    });

    this.collectionFormsControlsOrder = this.groupControlsBySuffix(this.collectionFormsControls);
    this.generateForm.formGroupControl = formGroupControl;
    this.formGroup = formGroupControl;
    this.generateForm = this.oderGenerateForm(this.generateForm);
    return { form: this.formGroup, collectionFormsControls: this.collectionFormsControls };
  }

  // Genera los errores que debe tener cada contrlador
  public generateError(errorInpusGeneral: ErrorInpusGeneral): FormControl {
    const control = new FormControl('');
    errorInpusGeneral.required ? control.addValidators(Validators.required) : null;
    errorInpusGeneral.maxlength
      ? control.addValidators(Validators.maxLength(errorInpusGeneral.maxlength))
      : null;
    errorInpusGeneral.minlength
      ? control.addValidators(Validators.minLength(errorInpusGeneral.minlength))
      : null;
    errorInpusGeneral.max ? control.addValidators(Validators.max(errorInpusGeneral.max)) : null;
    errorInpusGeneral.min ? control.addValidators(Validators.min(errorInpusGeneral.min)) : null;
    errorInpusGeneral.validationRegex
      ? control.addValidators(Validators.pattern(errorInpusGeneral.validationRegex))
      : null;
    errorInpusGeneral.regexr
      ? control.addValidators(this.createRegexrValidator(errorInpusGeneral.regexr))
      : null;
    errorInpusGeneral.isEmail ? control.addValidators(Validators.email) : null;
    return control;
  }

  private createRegexrValidator(regexr: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null =>
      !control.value || new RegExp(regexr).test(String(control.value))
        ? null
        : { errorGeneral: 'El formato es incorrecto' };
  }

  // funcion cambia las key para usar las reglas
  private groupControlsBySuffix(
    controls: Record<string, FormControl>,
  ): Record<string, FormControl> {
    const grouped: Record<string, FormControl> = {};

    Object.entries(controls).forEach(([key, value]) => {
      const parts = key.split('-');
      const suffix = parts.slice(-2).join('-'); // últimos dos segmentos como clave
      grouped[suffix] = value; // sobrescribe si ya existe
    });

    return grouped;
  }

  public upRulesForm(input: InputsGeneralForm, repeatIndex: number): boolean {
    const ruleKey = `${input.idControl}-${this.generateForm.formGroups!.idForm}`;
    const rule = this.ruleInputForm ? this.ruleInputForm[ruleKey] : undefined;
    if (rule && rule.disguise) {
      return false;
    }

    let isValid = false; // decidir si alguna regla permite habilitar
    if (input.rules && input.rules.length) {
      for (const ruleItem of input.rules) {
        const parts = ruleItem.parentFieldId ? String(ruleItem.parentFieldId).split('-') : [];
        const keyInputRule = parts.length > 0 ? parts[parts.length - 1] : ruleItem.parentFieldId;
        const controlEntry = this.collectionFormsControlsOrder[repeatIndex + '-' + keyInputRule];
        if (
          controlEntry &&
          (controlEntry.value === ruleItem.parentFieldValue ||
            (controlEntry.value != null &&
              ruleItem.parentFieldValue != null &&
              String(controlEntry.value) === String(ruleItem.parentFieldValue)))
        ) {
          isValid = true;
          break; // Basta con que una regla coincida
        }
      }

      // Aplicar habilitado/deshabilitado solo una vez
      if (input.disabled === false && (!rule || !rule.disabled)) {
        if (isValid && input.formControl?.disabled) {
          input.formControl.enable();
        } else if (!isValid && input.formControl?.enabled) {
          input.formControl.disable();
        }
      }
    } else {
      isValid = true; // si no hay reglas, permitir
    }

    return isValid;
  }

  public parseInput(input: string): { command: string; args: string[] } {
    // Buscar el índice del primer paréntesis '('
    const parenIndex = input.indexOf('(');
    // Si no hay paréntesis, asumir que no hay argumentos
    if (parenIndex === -1) {
      return { command: input.trim(), args: [] };
    }
    // Separar el comando antes del paréntesis y los datos dentro de los paréntesis
    const command = input.substring(0, parenIndex).trim();
    const data = input.substring(parenIndex + 1, input.length - 1); // Excluir los paréntesis
    // Separar los argumentos por "||"
    const args = data.split('||').map((arg) => arg.trim()); // Eliminar espacios adicionales
    // Devolver el resultado como un objeto
    return { command, args };
  }

  // Funcion reservada para funciones espesificas como mi fiel
  public inputFunction(dataInput: string, field?: InputsGeneralForm): void {
    const multiformCount = this.generateForm.formGroups?.multifrom.length ?? 1;
    const ress = this.buttonFunctionService.inputFunction(
      dataInput,
      this.quotationId,
      field,
      multiformCount,
    );
    if (ress) {
      this.emitButton.emit(ress);
    }
  }

  // Indica si el botón debe verse como "eliminar" (función con la palabra delete)
  public isDeleteButton(dataInput?: string | null): boolean {
    return this.buttonFunctionService.isDeleteFunction(dataInput);
  }

  public emitForm(toll: Toll, formGroups: GeneralForm, categoriTile: string): void {
    toll.mess = categoriTile;
    if (
      toll.function === 'back' ||
      toll.function === 'scrollRight' ||
      toll.function === 'scrollLeft'
    ) {
      this.emitFunctionTool.emit({ toll: toll, formGroups: formGroups });
    }
    if (toll.function === 'saveFrom') {
      if (this.markInvalidControlsAndCheck()) {
        this.saveFrom();
        this.emitFunctionTool.emit({ toll: toll, formGroups: formGroups });
      } else {
        this.generalService.viewSnacAlert('Formulario Invalido', 'error', 'Revisa los campos');
      }
    }
  }

  public markInvalidControlsAndCheck(): boolean {
    let allValid = true;
    Object.values(this.collectionFormsControls).forEach((control) => {
      if (!control.disabled && control.invalid) {
        control.markAsTouched();
        control.markAsDirty();
        allValid = false;
      }
    });

    return allValid;
  }

  public oderGenerateForm(generateForm: FormGeneral): FormGeneral {
    if (generateForm.formGroups?.multifrom) {
      generateForm.formGroups.multifrom.forEach((multifrom) => {
        multifrom.fields.sort((a, b) => {
          const orderA = a.order ?? Number.MAX_SAFE_INTEGER; // Si no tiene 'order', darle un valor muy alto
          const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB; // Orden ascendente por 'order'
        });
      });
    }
    return generateForm; // Devolver el objeto modificado
  }

  public renderActivity(generateForm: FormGeneral): void {
    this.generateForm = generateForm;
  }

  public async saveFrom(hideMessage?: boolean, hideLoad?: boolean): Promise<boolean> {
    if (this.generateForm.formGroups?.idForm == 27) return false;

    if (hideLoad) {
      this.loadService = false;
    } else {
      this.loadService = true;
    }
    try {
      const changedFields: any[] = [];
      const changedFormsObservable: Observable<any>[] = [];
      const changedFilesObservable: Observable<any>[] = [];

      // --- construir changedFields / changedFilesObservable (igual que tu lógica) ---
      this.generateForm.formGroups?.multifrom?.forEach((group) => {
        group.fields.forEach((input) => {
          const key = input.idFullControl;
          const parts = key.split('-');
          const idSection = parts[0];
          const idForm = parts[1];
          const repeatIndex = parts[2];
          const idControl = parts[3];

          // Detectar campos modificados
          if (
            (input.formControl?.dirty || input.formControl?.touched) &&
            input.typeInput !== 'input-upload-ligh' &&
            input.typeInput !== 'input-general-autocomplete' &&
            input.formControl?.valid
          ) {
            changedFields.push({
              idCotizacion: this.quotationId,
              controlId: parseInt(idControl),
              newValue: input.formControl.value,
              roleType: input.roleType !== '0' ? input.roleType : '',
              repeatIndex: parseInt(repeatIndex),
            });
          }
          // Guardar nuevos inputs de autocompletado aunque estén vacíos
          if (
            (input.formControl?.dirty || input.formControl?.touched) &&
            input.typeInput === 'input-general-autocomplete'
          ) {
            const index = input.arrayData.findIndex(
              (item) => item.data === input.formControl?.value,
            );
            changedFields.push({
              idCotizacion: this.quotationId,
              controlId: parseInt(idControl),
              newValue: input.arrayData[index]?.Id ?? input.formControl.value,
              roleType: input.roleType !== '0' ? input.roleType : '',
              repeatIndex: parseInt(repeatIndex),
            });
          }

          // Detectar archivos modificados
          // Comentado por que ya se activo el auto guardado de archivos
          if (
            input.typeInput === 'input-upload-ligh' &&
            input.formControl?.touched &&
            input.function == 'XML'
          ) {
            const file: File = input.formControl.value?.[0];
            if (file) {
              const formData = new FormData();
              formData.append('file', file);
              const documentData = `{"QuotationId":${this.quotationId},"DocumentId":0,"QuotationDocId":${input.AdditionalValuesFiles[0].iD_DOCUMENTOS_COTIZACION},"CoverId":${input.AdditionalValuesFiles[0].iD_CARATULA},"Index":"","IndexValue":"","Description":${input.AdditionalValuesFiles[0].legacyFileName},"UserId":${localStorage.getItem('idUser') || 1}}`;
              formData.append('documentData', documentData);
              changedFilesObservable.push(this.dynamicPageService.uploadDocument(formData));
            }
          } else if (input.typeInput === 'input-upload-ligh' && input.formControl?.touched) {
            const file: File = input.formControl.value?.[0];
            if (file) {
              const formData = new FormData();
              formData.append('file', file);
              const documentData = `{"QuotationId":${this.quotationId},"DocumentId":0,"QuotationDocId":${input.AdditionalValuesFiles[0].iD_DOCUMENTOS_COTIZACION},"CoverId":${input.AdditionalValuesFiles[0].iD_CARATULA},"Index":"","IndexValue":"","Description":${input.AdditionalValuesFiles[0].legacyFileName},"UserId":${localStorage.getItem('idUser') || 1}}`;
              formData.append('documentData', documentData);
              changedFilesObservable.push(this.dynamicPageService.uploadDocument(formData));
            }
          }

          // Guardar nuevos inputs multiform aunque estén vacíos (si es repetible)
          if (
            (input.formControl?.value == '' || input.formControl?.value == null) &&
            this.generateForm.formGroups?.isRepeatable
          ) {
            changedFields.push({
              idCotizacion: this.quotationId,
              controlId: parseInt(idControl),
              newValue: '',
              roleType: input.roleType !== '0' ? input.roleType : '',
              repeatIndex: parseInt(repeatIndex),
            });
          }
        });
      });

      if (changedFields.length) {
        changedFormsObservable.push(this.dynamicPageService.saveFrom(changedFields));
      }

      if (changedFormsObservable.length) {
        await this.generalService.getServices(changedFormsObservable);
      }

      if (changedFilesObservable.length) {
        const seq = concat(...changedFilesObservable).pipe(
          concatMap((res) => of(res)),
          toArray(),
          catchError((err) => {
            return of([]);
          }),
        );

        await lastValueFrom(seq);
      }

      if (!hideMessage) {
        this.generalService.viewSnacAlert('Guardado', 'normal', 'Formulario Guardado');
      }
      this.loadService = false;
      return true;
    } catch (err) {
      this.generalService.viewSnacAlert('Error', 'error', 'Ocurio un Error');
      this.loadService = false;
      return false;
    }
  }

  public addMultiform(): void {
    this.saveFrom(true).then((ress) => {
      const formGroups = this.generateForm.formGroups;

      if (formGroups?.hide && formGroups?.multifrom.length === 1) {
        formGroups.hide = false;
        return;
      }

      const multifrom = formGroups!.multifrom;
      const indexMultifom = multifrom?.length || 0;
      const lastForm = multifrom[indexMultifom - 1 > 0 ? indexMultifom - 1 : 0];
      const newRepeatIndex = lastForm.repeatIndex + 1;
      const newFields = lastForm.fields.map((input) => {
        const parts = input.idFullControl.split('-');
        parts[2] = newRepeatIndex.toString();

        return {
          ...input,
          value: null,
          idFullControl: parts.join('-'),
          formControl: new FormControl(null, input.formControl?.validator || []),
        };
      });

      const newForm = {
        repeatIndex: newRepeatIndex,
        fields: newFields,
      };

      const orderDefault = formGroups?.orderByRule?.orderDefault;
      if (orderDefault && Object.keys(orderDefault).length) {
        newFields.forEach((input, inputIndex) => {
          input.order = orderDefault[input.idControl]
            ? orderDefault[input.idControl].order
            : 999 + inputIndex;
        });
        newFields.sort(
          (a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER),
        );
      }

      const sectionGroup = this.fb.group({});
      newForm.fields.forEach((inputField) => {
        sectionGroup.addControl(inputField.idFullControl, inputField.formControl);
      });

      multifrom.push(newForm);
      this.generalService.viewSnacAlert(
        'Nuevo formulario disponible',
        'normal',
        'Formulario agregado',
      );

      this.collectionFormsControlsOrder = this.groupControlsBySuffix(this.collectionFormsControls);
      this.emitButton.emit('addMultiform');
    });
  }

  public emitInputEdit(idForm: number, idControl: number) {
    this.dynamicPageService.emitInputEdit.emit({ idForm, idControl, idSection: this.idSection });
  }

  public getFileLigh(body: {
    index: number;
    formControlGeneral: FormControl | AbstractControl;
    AdditionalValuesFiles: AdditionalValuesFiles;
  }) {
    const files = body.formControlGeneral.value;
    if (files && files.length && files[body.index]) {
      this.dynamicPageService.viewFileLight(files[body.index], files[0].name);
      return;
    }

    this.loadService = true;
    this.dynamicPageService
      .getFile(body.AdditionalValuesFiles.systemFileName ?? '')
      .subscribe((ress) => {
        if (ress) {
          const filename = body.AdditionalValuesFiles.fileName; // O tomar del header 'Content-Disposition'
          const file = new File([ress], filename ?? '', {
            type: ress.type || 'application/octet-stream',
          });
          const updatedFiles = [...(body.formControlGeneral.value ?? []), file];
          body.formControlGeneral.setValue(updatedFiles);
          this.dynamicPageService.viewFileLight(
            body.formControlGeneral.value[body.index],
            body.formControlGeneral.value[0].name,
          );
        }
        this.loadService = false;
      });
  }

  public downloadFileLigh(body: {
    index: number;
    formControlGeneral: FormControl | AbstractControl;
    AdditionalValuesFiles: AdditionalValuesFiles;
  }) {
    const files = body.formControlGeneral.value;
    if (files && files.length && files[body.index]) {
      const file: File = files[body.index];
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }

    this.loadService = true;
    this.dynamicPageService
      .getFile(body.AdditionalValuesFiles.systemFileName ?? '')
      .subscribe((ress) => {
        if (ress) {
          const filename = body.AdditionalValuesFiles.fileName ?? 'archivo';
          const blob = new Blob([ress], { type: ress.type || 'application/octet-stream' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        }
        this.loadService = false;
      });
  }
}
